import React, { useState, useEffect, useCallback } from 'react';
import FeedbackSection from './FeedbackSection';
import ScaffoldPanel from './ScaffoldPanel';

const RightPanel = ({ erdState, apiKey, username, notificationCount, feedbackCount, quotaValue, isPublicView = false, getClassContext }) => {
  const [showScaffold, setShowScaffold] = useState(false);
  const [quotaBlockMessage, setQuotaBlockMessage] = useState(null); // set when quota = 0 on click

  // Per-environment quota state
  const [envQuota, setEnvQuota] = useState(null); // { limit, used, remaining }

  useEffect(() => { setShowScaffold(false); }, [erdState.feedbackId]);

  // Fetch env-specific quota whenever the selected environment or class changes
  const fetchEnvQuota = useCallback(async () => {
    const ctx = getClassContext ? getClassContext() : {};
    const classId = ctx?.class_id;
    const envId   = erdState.selectedEnvironment;

    if (!classId || !envId || envId === 'None' || isPublicView) {
      setEnvQuota(null);
      return;
    }

    try {
      const res = await fetch(
        `/api/quota/environment?class_id=${encodeURIComponent(classId)}&environment_id=${encodeURIComponent(envId)}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.success) setEnvQuota(data);
      }
    } catch {
      // silently ignore — fall back to global quota display
      setEnvQuota(null);
    }
  }, [erdState.selectedEnvironment, getClassContext, isPublicView]);

  useEffect(() => { fetchEnvQuota(); }, [fetchEnvQuota]);

  // Re-fetch after feedback is received so remaining count updates
  useEffect(() => {
    if (erdState.feedbackId) fetchEnvQuota();
  }, [erdState.feedbackId, fetchEnvQuota]);

  // Click handler: fresh quota check before passing to LLM
  const handleGetFeedback = useCallback(async () => {
    setQuotaBlockMessage(null);
    const ctx = getClassContext ? getClassContext() : {};
    const classId = ctx?.class_id;
    const envId   = erdState.selectedEnvironment;

    if (classId && envId && envId !== 'None') {
      try {
        const res = await fetch(
          `/api/quota/environment?class_id=${encodeURIComponent(classId)}&environment_id=${encodeURIComponent(envId)}`,
          { credentials: 'include' }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.limit !== -1 && data.remaining <= 0) {
            setQuotaBlockMessage('You have reached your feedback quota for this assignment.');
            return; // do NOT call LLM
          }
          // Update displayed quota from the fresh response
          setEnvQuota(data);
        }
      } catch {
        // network error — fall through and let the server enforce quota
      }
    }

    erdState.getFeedback();
  }, [erdState, getClassContext]);

  // Determine quota display values: prefer env-specific, fall back to global
  const displayLimit     = envQuota ? envQuota.limit     : quotaValue;
  const displayUsed      = envQuota ? envQuota.used      : feedbackCount;
  const displayRemaining = envQuota ? envQuota.remaining : (quotaValue === -1 ? -1 : quotaValue - feedbackCount);
  const quotaExceeded    = displayLimit !== -1 && displayRemaining <= 0;

  return (
    <div
      className="flex flex-col min-h-0"
      style={{ width: `${100 - erdState.leftPanelWidth}%` }}
    >
      {/* Header - Fixed at top */}
      <div className="p-6 flex-shrink-0 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-800 mb-4">AI Assistant</h2>

        {/* Notification count (only if non-zero) */}
        {erdState.userStats.notificationCount > 0 && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-sm text-blue-600">
              <strong>Unseen notifications:</strong> {notificationCount}
            </p>
          </div>
        )}

        {/* Environment Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select an environment:
          </label>
          <select
            value={erdState.selectedEnvironment}
            onChange={(e) => erdState.setSelectedEnvironment(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {[...new Set(erdState.environments)].map((env, index) => (
              <option key={`env-option-${index}-${env}`} value={env}>
                {env}
              </option>
            ))}
          </select>
        </div>

        {/* Public View Message */}
        {isPublicView && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4">
            <p className="text-gray-700 text-sm">View-only mode: Feedback generation is not available for public viewers.</p>
          </div>
        )}

        {/* API Key Warning */}
        {!isPublicView && !apiKey && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
            <p className="text-yellow-700 text-sm">You need to sign in and provide an OpenAI API Key to receive feedback</p>
          </div>
        )}

        {/* Quota Warning */}
        {!isPublicView && quotaExceeded && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            <p className="text-red-700 text-sm">
              You have used all {displayLimit} feedback request{displayLimit !== 1 ? 's' : ''} for{' '}
              <strong>{erdState.selectedEnvironment}</strong>. Contact your instructor for more.
            </p>
          </div>
        )}

        {/* Get Feedback Button + quota badge - Hidden for public users */}
        {!isPublicView && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={handleGetFeedback}
                disabled={!apiKey || !erdState.hasRequirements || erdState.isGettingFeedback || quotaExceeded}
                title={!erdState.hasRequirements ? 'No requirements configured for this environment' : undefined}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-md transition-colors duration-200"
              >
                {erdState.isGettingFeedback ? 'Getting feedback...' : 'Get AI Feedback'}
              </button>

              {/* Quota badge */}
              {displayLimit !== -1 ? (
                <span className={`shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-md border ${
                  quotaExceeded
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : displayRemaining <= 2
                    ? 'bg-amber-50 border-amber-200 text-amber-700'
                    : 'bg-green-50 border-green-200 text-green-700'
                }`}>
                  {displayRemaining}/{displayLimit} feedback quota remains
                </span>
              ) : (
                <span className="shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-md border bg-gray-50 border-gray-200 text-gray-500">
                  ∞ feedback quota
                </span>
              )}
            </div>
            {!erdState.hasRequirements && erdState.selectedEnvironment !== 'None' && (
              <p className="text-xs text-amber-600 mb-3 -mt-3">No requirements configured for this environment.</p>
            )}
          </>
        )}

        {/* Selected Relationship Display */}
        {erdState.selectedRelationship !== 'All' && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700">
              <strong>Selected relationship:</strong> {erdState.selectedRelationship}
            </p>
          </div>
        )}
      </div>

      {/* Scrollable Feedback Area */}
      <div className="flex-1 overflow-y-auto p-6 min-h-0">
        {/* Quota block message — shown instead of feedback when quota is 0 */}
        {quotaBlockMessage && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <span className="text-amber-500 text-lg leading-none mt-0.5">⚠️</span>
            <p className="text-amber-800 text-sm font-medium">{quotaBlockMessage}</p>
          </div>
        )}

        {erdState.feedbackItems.length > 0 && (
          <>
            <FeedbackSection erdState={erdState} />

            {/* Scaffold: button visible when scaffold stages exist; condition stage decides routing */}
            {!isPublicView && erdState.scaffoldStages?.length > 0 && !showScaffold && (
              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => setShowScaffold(true)}
                  style={{
                    flex: 1, padding: '10px 16px',
                    background: '#4f46e5', color: '#fff', border: 'none',
                    borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14,
                  }}
                >
                  I need more help
                </button>
                {displayLimit !== -1 ? (
                  <span className={`shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-md border ${
                    quotaExceeded
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : displayRemaining <= 2
                      ? 'bg-amber-50 border-amber-200 text-amber-700'
                      : 'bg-green-50 border-green-200 text-green-700'
                  }`}>
                    {displayRemaining}/{displayLimit} feedback quota remains
                  </span>
                ) : (
                  <span className="shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-md border bg-gray-50 border-gray-200 text-gray-500">
                    ∞ feedback quota
                  </span>
                )}
              </div>
            )}

            {!isPublicView && showScaffold && (
              <ScaffoldPanel
                erdState={erdState}
                sectionId={erdState.selectedEnvironment}
                environmentId={erdState.selectedEnvironment}
                getClassContext={getClassContext}
                initialStageId={erdState.scaffoldStages?.[0]?.stage_id || 'stage_1'}
                onRevise={() => setShowScaffold(false)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RightPanel;
